import { Injectable, inject } from '@angular/core';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import {
  InvoiceListResponse,
  PaymentAttentionResponse,
  PaymentListResponse,
} from '../../../Interfaces/api/backoffice/billing-document.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import { PagedResult } from '../../Shared/Interfaces/paged-result.interface';
import { InvoiceSummary } from '../../../Models/backoffice/invoice-summary.model';
import { PaymentSummary } from '../../../Models/backoffice/payment-summary.model';

@Injectable({
  providedIn: 'root',
})
export class OrganizationBillingService {
  private readonly _apiService = inject(ApiService);

  invoicesRequest(
    organizationId: string,
    stripeStatus: string,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<InvoiceListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<InvoiceListResponse>>(
      endpoints.backoffice.organizationInvoices(organizationId),
      {
        params: this._params({ stripe_status: stripeStatus }, page, pageSize),
        skipLoading: true,
        skipError: true,
      },
    );
  }

  paymentsRequest(
    organizationId: string,
    stripeStatus: string,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<PaymentListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<PaymentListResponse>>(
      endpoints.backoffice.organizationPayments(organizationId),
      {
        params: this._params({ stripe_status: stripeStatus }, page, pageSize),
        skipLoading: true,
        skipError: true,
      },
    );
  }

  attentionRequest(
    organizationId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<PaymentAttentionResponse>> {
    return this._apiService.get<LaravelDefaultResponse<PaymentAttentionResponse>>(
      endpoints.backoffice.organizationPaymentsAttention(organizationId),
      { skipLoading: true, skipError: true },
    );
  }

  toInvoices(
    response: LaravelDefaultResponse<InvoiceListResponse> | undefined,
  ): PagedResult<InvoiceSummary> {
    const payload = response?.data;

    return {
      items: (payload?.invoices ?? []).map((row) => new InvoiceSummary(row)),
      total: payload?.total ?? 0,
      page: payload?.page ?? 0,
      pageSize: payload?.pageSize ?? 0,
    };
  }

  toPayments(
    response: LaravelDefaultResponse<PaymentListResponse> | undefined,
  ): PagedResult<PaymentSummary> {
    const payload = response?.data;

    return {
      items: (payload?.payments ?? []).map((row) => new PaymentSummary(row)),
      total: payload?.total ?? 0,
      page: payload?.page ?? 0,
      pageSize: payload?.pageSize ?? 0,
    };
  }

  toAttention(
    response: LaravelDefaultResponse<PaymentAttentionResponse> | undefined,
  ): PaymentAttentionResponse['attention'] | null {
    return response?.data?.attention ?? null;
  }

  private _params(
    filters: Record<string, string>,
    page: number,
    pageSize: number,
  ): Record<string, string | number> {
    const params: Record<string, string | number> = { page, pageSize };

    for (const [key, value] of Object.entries(filters)) {
      if (value !== '') {
        params[key] = value;
      }
    }

    return params;
  }
}
